import { AccountBalanceDto, AccountDto, BaseAccountDto } from 'src/dtos';
import {
  Account,
  AccountFinancialAssets,
  AccountHolderAddress,
  AccountStatus,
  AccountSuitabilityInformation,
  AccountType,
  BaseAccount,
  EntityAccountType,
  JointAccountType,
  LinkAccountStatus,
  RetirementAccountType,
} from 'src/models';
import { getUnitedStateOptionLabel } from 'src/utils';

import { mapAccountHolderDtoToModel } from './accountHolder.mapper';

const getCustomTypeLabel = ({
  type,
  retirementAccountType,
  entityAccountType,
}: {
  type: AccountType;
  retirementAccountType?: RetirementAccountType;
  entityAccountType?: EntityAccountType;
}) => {
  if (type.isRetirement) {
    return retirementAccountType?.label ?? type.label;
  }

  if (type.isEntity) {
    return entityAccountType?.label ?? type.label;
  }

  return type.label;
};

export const mapBaseAccountDtoToModel = (dto: BaseAccountDto, balance?: AccountBalanceDto): BaseAccount => {
  const type = new AccountType(dto.type);
  const jointAccountType = type.isJoint ? new JointAccountType(dto.jointAccountType) : undefined;
  const retirementAccountType = type.isRetirement ? new RetirementAccountType(dto.retirementAccountType) : undefined;
  const entityAccountType = type.isEntity ? new EntityAccountType(dto.entityAccountType) : undefined;

  return {
    id: dto.id,
    userId: dto.userId,
    accountId: dto.accountId,
    accountNumber: dto.accountNumber,
    status: new AccountStatus(dto.status),
    linkStatus: new LinkAccountStatus({
      accountId: dto.accountId,
      lastFailedLinkAccountError: dto.lastFailedLinkAttemptError,
    }),
    type: new AccountType(dto.type),
    customTypeLabel: getCustomTypeLabel({
      type,
      retirementAccountType,
      entityAccountType,
    }),
    isRoot: dto.isRoot,
    isAccredited: dto.isAccredited ?? false,
    primaryAccountHolder: dto.primaryAccountHolder && mapAccountHolderDtoToModel(dto.primaryAccountHolder),
    jointAccountType,
    retirementAccountType,
    entityAccountType,
    failedCreateAttempts: dto.failedCreateAttempts,
    lastFailedCreateAttemptError: dto.lastFailedCreateAttemptError,
    lastFailedCreateAttemptAt: dto.lastFailedCreateAttemptAt,
    failedLinkAttempts: dto.failedLinkAttempts,
    lastFailedLinkAttemptError: dto.lastFailedLinkAttemptError,
    lastFailedLinkAttemptAt: dto.lastFailedLinkAttemptAt,
    cashBalance: balance?.cashBalance,
    createdAt: dto.createdAt,
  };
};

export const mapAccountDtoToModel = (dto: AccountDto): Account => {
  const type = new AccountType(dto.type);
  const jointAccountType = type.isJoint ? new JointAccountType(dto.jointAccountType) : undefined;
  const retirementAccountType = type.isRetirement ? new RetirementAccountType(dto.retirementAccountType) : undefined;
  const entityAccountType = type.isEntity ? new EntityAccountType(dto.entityAccountType) : undefined;

  let secondaryAccountHolder = dto.secondaryAccountHolder;

  if (type.isEntity && dto.otherAccountHolders && dto.otherAccountHolders.length > 0) {
    secondaryAccountHolder = dto.otherAccountHolders[0];
  }

  return {
    id: dto.id,
    userId: dto.userId,
    type: new AccountType(dto.type),
    customTypeLabel: getCustomTypeLabel({
      type,
      retirementAccountType,
      entityAccountType,
    }),
    accountId: dto.accountId,
    accountNumber: dto.accountNumber,
    isRoot: dto.isRoot,
    isAccredited: dto.isAccredited ?? false,
    primaryAccountHolder: dto.primaryAccountHolder && mapAccountHolderDtoToModel(dto.primaryAccountHolder),
    secondaryAccountHolder: secondaryAccountHolder && mapAccountHolderDtoToModel(secondaryAccountHolder), // TODO: pass authroized signer 2 as secondary account holder if found in enttiy account
    jointAccountType,
    jointCommunityPropertyState: dto.jointCommunityPropertyState
      ? { label: getUnitedStateOptionLabel(dto.jointCommunityPropertyState), value: dto.jointCommunityPropertyState }
      : undefined,
    retirementAccountType,
    simpleIraPlanName: dto.simpleIraPlanName,
    beneficiaryIraDecedentName: dto.beneficiaryIraDecedentName,
    beneficiaries: dto.beneficiaries ?? [],
    status: new AccountStatus(dto.status),
    linkStatus: new LinkAccountStatus({
      accountId: dto.accountId,
      lastFailedLinkAccountError: dto.lastFailedLinkAttemptError,
    }),
    entityAccountType,
    entityInformation: dto.entityInformation
      ? { ...dto.entityInformation, address: new AccountHolderAddress(dto.entityInformation.address) }
      : undefined,
    applicationId: dto.applicationId,
    otherAccountHolders:
      dto.otherAccountHolders?.filter((holder, index) => index > 0).map(mapAccountHolderDtoToModel) ?? [],
    suitabilityInformation: dto.suitabilityInformation
      ? new AccountSuitabilityInformation(dto.suitabilityInformation)
      : undefined,
    financialAssets: dto.financialAssets ? new AccountFinancialAssets(dto.financialAssets) : undefined,
    failedCreateAttempts: dto.failedCreateAttempts,
    lastFailedCreateAttemptError: dto.lastFailedCreateAttemptError,
    lastFailedCreateAttemptAt: dto.lastFailedCreateAttemptAt,
    failedLinkAttempts: dto.failedLinkAttempts,
    lastFailedLinkAttemptError: dto.lastFailedLinkAttemptError,
    lastFailedLinkAttemptAt: dto.lastFailedLinkAttemptAt,
    updatedAt: dto.updatedAt,
    createdAt: dto.createdAt,
  };
};
